import React from 'react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { dashboardMuted } from '../../theme/dashboardLayout';
import {
  COMPLETENESS_PREVIOUS_RADAR_COLOR,
  COMPLETENESS_RADAR_COLOR,
  ScanCompletenessSlice,
  ScanCompletenessValue,
  hasPreviousCompleteness,
  radarPercentDomain,
  scanCompletenessSlices,
} from './scanCompleteness';

type RadarTooltipProps = {
  active?: boolean;
  payload?: Array<{ payload?: ScanCompletenessSlice }>;
};

function peopleLine(count: number | null | undefined): string {
  if (count == null) {
    return '';
  }
  return count === 1 ? '1 person' : `${count} people`;
}

export function CompletenessRadarTooltip({
  active,
  payload,
}: RadarTooltipProps): React.ReactElement | null {
  if (!active) {
    return null;
  }

  const slice = payload?.[0]?.payload;
  if (!slice) {
    return null;
  }

  const previous = slice.previousPercent != null;

  return (
    <div className="rounded-xl border border-portal-teal/15 bg-pure-white px-3 py-2 shadow-[0_8px_24px_-8px_rgba(0,75,77,0.18)]">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold text-portal-teal">
        <span
          aria-hidden
          className="inline-block h-2 w-2 shrink-0 rounded-sm"
          style={{ backgroundColor: slice.color }}
        />
        {slice.label}
      </p>
      {previous ? (
        <>
          <p className="mt-0.5 text-[11px] leading-tight text-portal-teal/80">
            {`Last 28 days: ${peopleLine(slice.count)} · ${slice.percent}%`}
          </p>
          <p className="text-[11px] leading-tight text-portal-teal/70">
            {`Previous 28 days: ${peopleLine(slice.previousCount)} · ${slice.previousPercent}%`}
          </p>
        </>
      ) : (
        <>
          <p className="mt-0.5 text-[11px] leading-tight text-portal-teal/80">{peopleLine(slice.count)}</p>
          <p className="text-[11px] leading-tight text-portal-teal/70">{`${slice.percent}% of enrolled`}</p>
        </>
      )}
    </div>
  );
}

const ScanCompletenessPie = ({
  completeness,
}: {
  completeness: ScanCompletenessValue;
}): React.ReactElement => {
  const slices = React.useMemo(
    () => scanCompletenessSlices(completeness),
    [completeness],
  );
  const showPrevious = hasPreviousCompleteness(slices);
  const radiusDomain = React.useMemo(
    () =>
      radarPercentDomain([
        ...slices.map((slice) => slice.percent),
        ...slices
          .map((slice) => slice.previousPercent)
          .filter((percent): percent is number => percent != null),
      ]),
    [slices],
  );
  const hasMembers = completeness.denominator > 0;

  return (
    <div className="relative mt-2 -mx-1 w-full min-w-0 sm:mx-0">
      <div className="h-[min(16rem,54vw)] w-full min-h-[200px] sm:h-60 lg:h-56">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={slices} cx="50%" cy="50%" outerRadius="80%">
            <PolarGrid stroke="rgba(0,75,77,0.28)" />
            <PolarAngleAxis dataKey="label" tick={{ fill: '#004b4d', fontSize: 10 }} />
            <PolarRadiusAxis domain={radiusDomain} tick={false} axisLine={false} />
            {showPrevious ? (
              <Radar
                name="Previous 28 days"
                dataKey="previousPercent"
                stroke={COMPLETENESS_PREVIOUS_RADAR_COLOR}
                fill={COMPLETENESS_PREVIOUS_RADAR_COLOR}
                fillOpacity={0.18}
                isAnimationActive={false}
              />
            ) : null}
            <Radar
              name="Last 28 days"
              dataKey="percent"
              stroke={COMPLETENESS_RADAR_COLOR}
              fill={COMPLETENESS_RADAR_COLOR}
              fillOpacity={0.35}
              isAnimationActive={false}
            />
            <Tooltip
              content={<CompletenessRadarTooltip />}
              cursor={false}
              offset={10}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {showPrevious ? (
        <ul className="mt-1 flex min-h-[1.25rem] flex-wrap content-start items-center justify-center gap-x-4 gap-y-1 px-1">
          <li className="flex items-center gap-1.5 text-[11px] text-[#004b4d]">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: COMPLETENESS_RADAR_COLOR }}
            />
            <span>Last 28 days</span>
          </li>
          <li className="flex items-center gap-1.5 text-[11px] text-[#004b4d]">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: COMPLETENESS_PREVIOUS_RADAR_COLOR }}
            />
            <span>Previous 28 days</span>
          </li>
        </ul>
      ) : null}
      <ul className="mt-1 flex min-h-[2.25rem] flex-wrap content-start items-center justify-center gap-x-3 gap-y-1 px-1">
        {slices.map((slice) => (
          <li key={slice.key} className="flex items-center gap-1.5 text-[10px] leading-tight text-[#004b4d]">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: slice.color }}
            />
            <span>{`${slice.label}  ${slice.percent}%`}</span>
          </li>
        ))}
      </ul>
      {!hasMembers ? (
        <p className={`mt-2 text-center ${dashboardMuted}`}>No enrolled people to chart</p>
      ) : null}
    </div>
  );
};

export default ScanCompletenessPie;
